import React, {useEffect, useMemo, useRef, useState} from 'react';
import {observer} from "mobx-react-lite";

function NewFileThumb({file, onRemove, itemClassName = ''}) {
    const url = useMemo(() => URL.createObjectURL(file), [file])
    useEffect(() => () => URL.revokeObjectURL(url), [url])
    return (
        <li
            className={`relative aspect-square overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm ${itemClassName}`}
        >
            <img src={url} alt="" className="h-full w-full object-cover"/>
            <span className="pointer-events-none absolute bottom-1 left-1 rounded bg-pine-800/80 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                Новое
            </span>
            <button
                type="button"
                className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-bark-900/75 text-sm text-white backdrop-blur-sm hover:bg-red-600"
                onClick={onRemove}
                aria-label="Убрать файл"
            >
                ×
            </button>
        </li>
    )
}

/**
 * @param {'create'|'update'} variant
 * @param {{filename: string, url: string}[]} [existingPreviews]
 * @param {string[]} [keptFilenames]
 * @param {function} [setKeptFilenames]
 * @param {File[]} uploadFiles
 * @param {function} setUploadFiles
 */
const ItemForm = observer(
    ({
        show,
        onHide,
        sendRequest,
        item,
        setItem,
        items,
        variant = 'create',
        existingPreviews = [],
        keptFilenames = [],
        setKeptFilenames,
        uploadFiles,
        setUploadFiles,
    }) => {
        const fileRef = useRef(null)
        const photoDropDepthRef = useRef(0)
        const [photoDropActive, setPhotoDropActive] = useState(false)

        const appendImageFiles = (fileList) => {
            const add = Array.from(fileList || []).filter((f) => f.type?.startsWith('image/'))
            if (add.length) {
                setUploadFiles((prev) => [...prev, ...add])
            }
        }

        const handlePriceChange = (e) => {
            if (/^\d*$/.test(e.target.value))
                setItem({...item, price: e.target.value === '' ? 0 : Number(e.target.value)})
        }

        const handleCountChange = (e) => {
            if (/^\d*$/.test(e.target.value))
                setItem({...item, count: e.target.value === '' ? 0 : Number(e.target.value)})
        }

        const onPickFiles = (e) => {
            appendImageFiles(e.target.files)
            e.target.value = ''
        }

        const onPhotoDragEnter = (e) => {
            e.preventDefault()
            e.stopPropagation()
            photoDropDepthRef.current += 1
            setPhotoDropActive(true)
        }

        const onPhotoDragLeave = (e) => {
            e.preventDefault()
            e.stopPropagation()
            photoDropDepthRef.current -= 1
            if (photoDropDepthRef.current <= 0) {
                photoDropDepthRef.current = 0
                setPhotoDropActive(false)
            }
        }

        const onPhotoDragOver = (e) => {
            e.preventDefault()
            e.stopPropagation()
            e.dataTransfer.dropEffect = 'copy'
        }

        const onPhotoDrop = (e) => {
            e.preventDefault()
            e.stopPropagation()
            photoDropDepthRef.current = 0
            setPhotoDropActive(false)
            appendImageFiles(e.dataTransfer.files)
        }

        const removeNewFileAt = (index) => {
            setUploadFiles((prev) => prev.filter((_, j) => j !== index))
        }

        const removeExisting = (filename) => {
            if (setKeptFilenames) {
                setKeptFilenames((prev) => prev.filter((f) => f !== filename))
            }
        }

        useEffect(() => {
            if (!show) {
                photoDropDepthRef.current = 0
                setPhotoDropActive(false)
            }
        }, [show])

        if (!show) return null

        const isCreate = variant === 'create'

        const visibleExisting = (existingPreviews || []).filter((p) =>
            keptFilenames.includes(p.filename),
        )

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <button
                    type="button"
                    className="absolute inset-0 bg-bark-900/40 backdrop-blur-sm"
                    aria-label="Закрыть"
                    onClick={onHide}
                />
                <div
                    className="relative flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-stone-200/80 bg-white shadow-2xl">
                    <div className="flex shrink-0 items-start justify-between gap-4 border-b border-stone-100 px-5 pb-4 pt-5 sm:px-6 sm:pt-6">
                        <h2 className="text-xl font-semibold text-bark-900">
                            {isCreate ? 'Новый товар' : 'Обновить товар'}
                        </h2>
                        <button
                            type="button"
                            onClick={onHide}
                            className="rounded-full p-2 text-bark-500 hover:bg-cream-100"
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
                        <div className="flex flex-col gap-4 sm:gap-5">
                            <div>
                                <label className="lut-label">Название</label>
                                <input
                                    className="lut-input"
                                    value={item.title}
                                    onChange={(e) => setItem({...item, title: e.target.value})}
                                />
                            </div>

                            <div className="grid gap-5 sm:grid-cols-3">
                                <div>
                                    <label className="lut-label">Количество</label>
                                    <input
                                        className="lut-input"
                                        placeholder="Количество"
                                        value={item.count}
                                        onChange={handleCountChange}
                                    />
                                </div>
                                <div>
                                    <label className="lut-label">Цена</label>
                                    <div className="flex rounded-2xl border border-stone-200/90 bg-white shadow-sm focus-within:border-pine-500 focus-within:ring-2 focus-within:ring-pine-500/20">
                                        <input
                                            className="min-w-0 flex-1 rounded-l-2xl border-0 bg-transparent px-4 py-3 outline-none"
                                            placeholder="Цена"
                                            value={item.price}
                                            onChange={handlePriceChange}
                                        />
                                        <span className="flex items-center rounded-r-2xl bg-stone-100 px-3 text-sm font-medium text-bark-600">
                                            ₽
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="lut-label">Категория</label>
                                    <select
                                        className="lut-input"
                                        value={item.category || ''}
                                        onChange={(e) => setItem({...item, category: e.target.value})}
                                    >
                                        <option value="">Выберите</option>
                                        {items.categories.map((category) => (
                                            <option key={category.title} value={category.title}>
                                                {category.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="lut-label">Описание</label>
                                <textarea
                                    className="lut-input min-h-[128px] resize-y"
                                    rows={5}
                                    value={item.description}
                                    onChange={(e) => setItem({...item, description: e.target.value})}
                                />
                            </div>

                            <div
                                className={`rounded-2xl border p-4 transition-colors ${
                                    photoDropActive
                                        ? 'border-pine-500 bg-pine-50/50 ring-2 ring-pine-500/25'
                                        : 'border-stone-200/60 bg-cream-50/40'
                                }`}
                                onDragEnter={onPhotoDragEnter}
                                onDragLeave={onPhotoDragLeave}
                                onDragOver={onPhotoDragOver}
                                onDrop={onPhotoDrop}
                            >
                                <label className="lut-label mb-2 block w-full" htmlFor="item-form-photo-input">
                                    Фотографии
                                </label>
                                <p className="mb-3 text-xs leading-relaxed text-bark-500">
                                    Перетащите изображения сюда или выберите файлы.
                                </p>
                                <input
                                    id="item-form-photo-input"
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="sr-only"
                                    onChange={onPickFiles}
                                />
                                <button
                                    type="button"
                                    className="lut-btn-ghost text-sm"
                                    onClick={() => fileRef.current?.click()}
                                >
                                    Выбрать файлы
                                </button>
                                {(visibleExisting.length > 0 || uploadFiles.length > 0) && (
                                    <ul className="mx-auto mt-4 grid max-w-md grid-cols-3 gap-3 sm:grid-cols-4">
                                        {visibleExisting.map((p) => (
                                            <li
                                                key={`ex-${p.filename}`}
                                                className="relative aspect-square overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm ring-1 ring-stone-100"
                                            >
                                                <img
                                                    src={p.url}
                                                    alt=""
                                                    className="h-full w-full object-cover"
                                                />
                                                <span className="pointer-events-none absolute bottom-1 left-1 rounded bg-bark-900/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                                                    Сейчас
                                                </span>
                                                <button
                                                    type="button"
                                                    className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-bark-900/75 text-sm text-white backdrop-blur-sm transition hover:bg-red-600"
                                                    onClick={() => removeExisting(p.filename)}
                                                    aria-label="Убрать фото"
                                                >
                                                    ×
                                                </button>
                                            </li>
                                        ))}
                                        {uploadFiles.map((f, i) => (
                                            <NewFileThumb
                                                key={`new-${f.name}-${i}-${f.lastModified}`}
                                                file={f}
                                                onRemove={() => removeNewFileAt(i)}
                                                itemClassName="border-pine-200/90 ring-1 ring-pine-100"
                                            />
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex shrink-0 justify-end gap-3 border-t border-stone-100 bg-white px-5 py-4 sm:px-6">
                        <button type="button" onClick={onHide} className="lut-btn-ghost">
                            Отмена
                        </button>
                        <button type="button" onClick={sendRequest} className="lut-btn-pine">
                            {isCreate ? 'Создать' : 'Обновить'}
                        </button>
                    </div>
                </div>
            </div>
        )
    },
)

export default ItemForm
